import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";

import {
  calculatePayrollPeriodApi,
  closePayrollPeriodApi,
  confirmRemainingPayrollMonthInputsApi,
  createPayrollEntryApi,
  deletePayrollEntryApi,
  DpPayrollEntry,
  DpPayrollItem,
  DpPayrollMonthInput,
  DpPayrollPeriod,
  DpPayrollPeriodResult,
  DpPayrollPixCopy,
  DpPayrollResultStatus,
  competenceSlug,
  exportPayrollPeriodApi,
  formatPayrollCompetence,
  getPayslipPdfApi,
  getPayslipsBatchPdfApi,
  getAdvanceReceiptPdfApi,
  getAdvanceReceiptsBatchPdfApi,
  getPayrollPeriodApi,
  isThirteenth,
  listPayrollEntriesApi,
  listPayrollItemsApi,
  listPayrollMonthInputsApi,
  markPayrollPixCopiedApi,
  PAYROLL_ITEM_KIND_LABELS,
  PAYROLL_KIND_LABELS,
  PAYROLL_RESULT_STATUS_BADGE,
  PAYROLL_RESULT_STATUS_LABELS,
  reopenPayrollPeriodApi,
  unmarkPayrollPixCopiedApi,
  updatePayrollEntryApi,
  updatePayrollPeriodApi,
} from "../api/payrollApi";
import ReasonModal from "../components/ReasonModal";
import MonthInputModal from "../components/MonthInputModal";
import DocumentFileButton from "../components/DocumentFileButton";
import {
  downloadBlob,
  formatCpf,
  formatCurrency,
  formatDateOnly,
  toDateInputValue,
} from "../utils/format";
import { DP_PAYROLL_PAGE, DP_PAYROLL_RESULT_PAGE, DP_PAGE, DP_PROVISIONS_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";
import FinanceBridgeBox from "../components/FinanceBridgeBox";

type Tab = "resultados" | "lancamentos";
type StatusFilter = "TODOS" | DpPayrollResultStatus;

/** Situações sem contracheque: valor inventado (sem apuração) ou incompleto (pendente). */
const PAYSLIP_BLOCKED: Partial<Record<DpPayrollResultStatus, string>> = {
  SEM_PONTO: "Apure o mês e recalcule para gerar o contracheque",
  PENDENTE: "Resolva as pendências do cálculo para gerar o contracheque",
  RESCINDIDO: "Desligado no mês: o pagamento está no demonstrativo de rescisão (ficha → Contratos)",
};

const emptyEntry = { contractId: "", itemId: "", amount: "", quantity: "", note: "" };

/** Resumo curto da apuração manual para a lista: "25 dias · 42,2 h HE 50%". */
const describeMonthInput = (m: DpPayrollMonthInput) => {
  const h = (v: string | null) => (v === null ? null : Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 }));
  const parts = [
    m.workedDays !== null ? `${m.workedDays} dias` : null,
    m.absenceDays ? `${m.absenceDays} falta(s)` : null,
    m.overtimeWorkdayHours ? `${h(m.overtimeWorkdayHours)} h HE 50%` : null,
    m.overtimeRestDayHours ? `${h(m.overtimeRestDayHours)} h HE 100%` : null,
    m.nightHours ? `${h(m.nightHours)} h noturno` : null,
    m.lateMinutes ? `${m.lateMinutes} min atraso` : null,
  ].filter(Boolean);
  return `Apuração: ${parts.join(" · ") || "sem ocorrências"}`;
};

export default function PayrollPeriodPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAYROLL_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [period, setPeriod] = useState<DpPayrollPeriod | null>(null);
  const [entries, setEntries] = useState<DpPayrollEntry[]>([]);
  const [items, setItems] = useState<DpPayrollItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [pixBusy, setPixBusy] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("resultados");
  const [status, setStatus] = useState<StatusFilter>("TODOS");
  const [query, setQuery] = useState("");
  const [dates, setDates] = useState({ advanceDate: "", paymentDate: "" });
  const [reopening, setReopening] = useState(false);
  const [entryModal, setEntryModal] = useState<{ entry?: DpPayrollEntry } | null>(null);
  const [entryForm, setEntryForm] = useState(emptyEntry);
  // Apuração manual do mês, por contrato (sem ponto, o DP digita os totais).
  const [monthInputs, setMonthInputs] = useState<Record<string, DpPayrollMonthInput>>({});
  const [monthInputModal, setMonthInputModal] = useState<{ contractId: string; employeeName: string } | null>(null);

  const load = useCallback(async () => {
    if (!token || !id) return;
    try {
      const [p, e, m] = await Promise.all([
        getPayrollPeriodApi(token, id),
        listPayrollEntriesApi(token, id),
        listPayrollMonthInputsApi(token, id),
      ]);
      setPeriod(p);
      setEntries(e);
      setMonthInputs(Object.fromEntries(m.map((x) => [x.contractId, x])));
      setDates({ advanceDate: toDateInputValue(p.advanceDate), paymentDate: toDateInputValue(p.paymentDate) });
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar a competência.");
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    listPayrollItemsApi(token)
      .then(setItems)
      .catch(() => setItems([]));
  }, [token]);

  const isOpen = period?.status === "ABERTO";
  // 13º (F2a): sem adiantamento e sem apuração do mês; o resto da tela é igual.
  const thirteenth = !!period && isThirteenth(period);
  const manualItems = useMemo(() => items.filter((i) => i.isManual && i.isActive), [items]);

  const visible = useMemo(() => {
    if (!period) return [];
    const q = query.trim().toLowerCase();
    const digits = q.replace(/\D/g, "");
    return period.results.filter(
      (r) =>
        (status === "TODOS" || r.status === status) &&
        (!q ||
          r.meta.fullName.toLowerCase().includes(q) ||
          (digits.length > 0 && (r.meta.cpf ?? "").includes(digits)) ||
          (r.meta.registrationNumber ?? "").toLowerCase() === q)
    );
  }, [period, status, query]);

  // Atualiza só a linha: recarregar a competência inteira a cada cópia pesaria na lista.
  const setPixCopy = (contractId: string, pixCopy: DpPayrollPixCopy | null) =>
    setPeriod((p) =>
      p ? { ...p, results: p.results.map((r) => (r.contractId === contractId ? { ...r, pixCopy } : r)) } : p
    );

  const markPix = async (r: DpPayrollPeriodResult) => {
    if (!token || !period) return;
    setPixBusy(r.contractId);
    try {
      setPixCopy(r.contractId, await markPayrollPixCopiedApi(token, period.id, r.contractId));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao marcar o PIX como copiado.");
    } finally {
      setPixBusy(null);
    }
  };

  const copyPix = async (r: DpPayrollPeriodResult) => {
    if (!r.pixKey) return;
    try {
      // `clipboard` não existe em contexto não seguro (http sem localhost).
      await navigator.clipboard.writeText(r.pixKey);
    } catch {
      toast.info("Não foi possível copiar: selecione a chave e copie manualmente.");
      return;
    }
    toast.success(`PIX de ${r.meta.fullName} copiado.`);
    if (!r.pixCopy) await markPix(r);
  };

  const togglePixCopy = async (r: DpPayrollPeriodResult) => {
    if (!r.pixCopy) return markPix(r);
    if (!token || !period) return;
    if (!window.confirm(`Desmarcar o PIX de ${r.meta.fullName} como copiado?`)) return;
    setPixBusy(r.contractId);
    try {
      await unmarkPayrollPixCopiedApi(token, period.id, r.contractId);
      setPixCopy(r.contractId, null);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao desmarcar o PIX.");
    } finally {
      setPixBusy(null);
    }
  };

  const run = async (label: string, action: () => Promise<unknown>, success: string) => {
    setBusy(label);
    try {
      await action();
      toast.success(success);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao executar a ação.");
    } finally {
      setBusy(null);
    }
  };

  const calculate = () => token && run("calc", () => calculatePayrollPeriodApi(token, id), "Competência calculada.");

  const close = () => {
    if (!token || !period) return;
    const pending = period.summary.byStatus.PENDENTE;
    if (period.summary.withoutInput > 0) {
      return toast.error(`${period.summary.withoutInput} colaborador(es) sem apuração do mês. Apure ou confirme "sem ocorrências" antes de fechar.`);
    }
    const message =
      `Fechar a competência ${formatPayrollCompetence(period)}? O sistema recalcula antes de fechar.` +
      (pending ? `\n\n${pending} colaborador(es) estão com pendência de cadastro.` : "");
    if (!window.confirm(message)) return;
    run("close", () => closePayrollPeriodApi(token, id), "Competência fechada.");
  };

  const confirmRemaining = () => {
    if (!token || !period) return;
    const n = period.summary.withoutInput;
    if (!window.confirm(`Confirmar "mês sem ocorrências" para ${n} colaborador(es) sem apuração? Depois, clique em Calcular.`)) return;
    run("confirm", () => confirmRemainingPayrollMonthInputsApi(token, id), "Apurações confirmadas. Clique em Calcular para aplicar.");
  };

  const saveDates = () =>
    token &&
    run(
      "dates",
      () =>
        updatePayrollPeriodApi(
          token,
          id,
          thirteenth ? { paymentDate: dates.paymentDate } : { advanceDate: dates.advanceDate, paymentDate: dates.paymentDate }
        ),
      "Datas salvas. Recalcule para aplicar."
    );

  const exportCsv = async (format: "resumo" | "analitico") => {
    if (!token || !period) return;
    setBusy(`csv-${format}`);
    try {
      const blob = await exportPayrollPeriodApi(token, id, format);
      downloadBlob(blob, `folha-${competenceSlug(period)}-${format}.csv`);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao exportar.");
    } finally {
      setBusy(null);
    }
  };

  const openEntry = (entry?: DpPayrollEntry) => {
    setEntryForm(
      entry
        ? {
            contractId: entry.contractId,
            itemId: entry.itemId,
            amount: String(Number(entry.amount)),
            quantity: entry.quantity === null ? "" : String(Number(entry.quantity)),
            note: entry.note ?? "",
          }
        : emptyEntry
    );
    setEntryModal({ entry });
  };

  const saveEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !entryModal) return;
    const amount = Number(entryForm.amount);
    if (!entryForm.contractId) return toast.error("Escolha o colaborador.");
    if (!entryForm.itemId) return toast.error("Escolha a rubrica.");
    if (!Number.isFinite(amount) || amount <= 0) return toast.error("Informe o valor (maior que zero).");
    const quantity = entryForm.quantity.trim() === "" ? null : Number(entryForm.quantity);
    const note = entryForm.note.trim() || null;

    setBusy("entry");
    try {
      if (entryModal.entry) {
        await updatePayrollEntryApi(token, entryModal.entry.id, { itemId: entryForm.itemId, amount, quantity, note });
      } else {
        await createPayrollEntryApi(token, id, { contractId: entryForm.contractId, itemId: entryForm.itemId, amount, quantity, note });
      }
      toast.success("Lançamento salvo. Recalcule para aplicar.");
      setEntryModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o lançamento.");
    } finally {
      setBusy(null);
    }
  };

  const removeEntry = (entry: DpPayrollEntry) => {
    if (!token) return;
    if (!window.confirm(`Excluir o lançamento de ${entry.item.name} para ${entry.contract.employee.fullName}?`)) return;
    run("entry", () => deletePayrollEntryApi(token, entry.id), "Lançamento excluído. Recalcule para aplicar.");
  };

  if (!token) return null;

  const notCalculated = period && !period.calculatedAt;
  const fgts = period?.reconciliation.fgts;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          {loading ? (
            <div className="dpEmpty">Carregando...</div>
          ) : !period ? (
            <div className="dpEmpty">Competência não encontrada.</div>
          ) : (
            <>
              <EcoPageHeader
                crumbs={[
                  { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
                  { label: "Folha", onClick: () => navigate(DP_PAYROLL_PAGE()) },
                  { label: formatPayrollCompetence(period) },
                ]}
                title={thirteenth ? `${PAYROLL_KIND_LABELS[period.kind]} de ${period.year}` : `Folha ${formatPayrollCompetence(period)}`}
                subtitle={`${
                  period.calculatedAt
                    ? `Calculada em ${new Date(period.calculatedAt).toLocaleString("pt-BR")}`
                    : "Ainda não calculada"
                }${
                  period.closedAt
                    ? ` · fechada em ${new Date(period.closedAt).toLocaleString("pt-BR")}${
                        period.closedBy ? ` por ${period.closedBy.nomeCompleto}` : ""
                      }`
                    : ""
                }`}
                onBack={goBack}
                backLabel="Voltar para a folha"
                actions={
                  <>
                    <span className={`dpBadge ${isOpen ? "dpBadge--blue" : "dpBadge--gray"} dpeHeaderBadge`}>
                      {isOpen ? "Aberta" : "Fechada"}
                    </span>
                    {isOpen ? (
                      <>
                        <button type="button" className="ecoBtn" onClick={close} disabled={!!busy || !!notCalculated}>
                          {busy === "close" ? "Fechando..." : thirteenth ? "Fechar parcela" : "Fechar mês"}
                        </button>
                        <button type="button" className="ecoBtn ecoBtn--primary" onClick={calculate} disabled={!!busy}>
                          {busy === "calc" ? "Calculando..." : "Calcular"}
                        </button>
                      </>
                    ) : (
                      <button type="button" className="ecoBtn" onClick={() => setReopening(true)} disabled={!!busy}>
                        Reabrir
                      </button>
                    )}
                  </>
                }
              />

              {period.reopenedAt ? (
                <p className="dpNotice dpNotice--warning">
                  Reaberta em {new Date(period.reopenedAt).toLocaleString("pt-BR")}
                  {period.reopenedBy ? ` por ${period.reopenedBy.nomeCompleto}` : ""}: {period.reopenReason}
                </p>
              ) : null}

              <section className="dpSection">
                <div className="dpFormGrid">
                  {thirteenth ? null : (
                    <div className="dpFormGroup">
                      <label htmlFor="advanceDate">Adiantamento pago em</label>
                      <input
                        id="advanceDate"
                        type="date"
                        className="dpInput"
                        value={dates.advanceDate}
                        onChange={(e) => setDates((d) => ({ ...d, advanceDate: e.target.value }))}
                        disabled={!isOpen || !!busy}
                      />
                    </div>
                  )}
                  <div className="dpFormGroup">
                    <label htmlFor="paymentDate">{thirteenth ? "13º pago em" : "Salário pago em"}</label>
                    <input
                      id="paymentDate"
                      type="date"
                      className="dpInput"
                      value={dates.paymentDate}
                      onChange={(e) => setDates((d) => ({ ...d, paymentDate: e.target.value }))}
                      disabled={!isOpen || !!busy}
                    />
                    <span className="dpFormHint">O IR usa a tabela vigente nesta data (regime de caixa).</span>
                  </div>
                  {isOpen ? (
                    <div className="dpFormGroup" style={{ justifyContent: "flex-end" }}>
                      <button
                        className="dpBtn dpBtn--secondary"
                        onClick={saveDates}
                        disabled={
                          !!busy ||
                          (dates.advanceDate === toDateInputValue(period.advanceDate) &&
                            dates.paymentDate === toDateInputValue(period.paymentDate))
                        }
                      >
                        Salvar datas
                      </button>
                    </div>
                  ) : null}
                </div>
              </section>

              {notCalculated ? (
                <p className="dpNotice dpNotice--warning">
                  Competência aberta e ainda não calculada. Clique em <strong>Calcular</strong> para gerar o
                  pagamento de cada colaborador.
                </p>
              ) : (
                <>
                  {period.summary.withoutInput > 0 ? (
                    <div className="dpNotice dpNotice--warning" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ flex: 1 }}>
                        <strong>Totais parciais.</strong> {period.summary.withoutInput} colaborador(es) ainda sem apuração do mês
                        — os valores deles não aparecem nem entram nos totais, e a competência não fecha. Clique em{" "}
                        <strong>Apurar mês</strong> em cada um, ou confirme de uma vez quem não teve ocorrência.
                      </span>
                      {isOpen ? (
                        <button className="dpBtn dpBtn--secondary" onClick={confirmRemaining} disabled={!!busy}>
                          {busy === "confirm" ? "Confirmando..." : "Confirmar sem ocorrências os restantes"}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="dpCounters">
                    {[
                      ["Colaboradores", String(period.summary.contracts)],
                      ["Bruto", formatCurrency(period.summary.gross)],
                      ["Descontos", formatCurrency(period.summary.deductions)],
                      ...(thirteenth ? [] : [["Adiantamento (já pago)", formatCurrency(period.summary.advance)]]),
                      ["Líquido final", formatCurrency(period.summary.net)],
                      ["INSS", formatCurrency(period.summary.inss)],
                      ["IRRF", formatCurrency(period.summary.irrf)],
                      ["FGTS (informativo)", formatCurrency(period.summary.fgts)],
                    ].map(([label, value]) => (
                      <div key={label} className="dpCounter">
                        <div className="dpCounter__value">{value}</div>
                        <div className="dpCounter__label">{label}</div>
                      </div>
                    ))}
                  </div>

                  {thirteenth ? (
                    <p className="dpNotice">
                      <strong>Conferência com as guias.</strong>{" "}
                      {period.kind === "DECIMO_TERCEIRO_1"
                        ? "A 1ª parcela não tem INSS nem IR."
                        : `INSS do 13º dos empregados ${formatCurrency(period.reconciliation.inss.employeesCalculated)}${
                            period.reconciliation.inss.darf !== null
                              ? ` · DARF da competência 13 ${formatCurrency(period.reconciliation.inss.darf)} (inclui a parte da empresa)`
                              : " · DARF da competência 13 ainda não lançado"
                          }.`}{" "}
                      O FGTS do 13º ({formatCurrency(fgts?.calculated)}) é recolhido na guia do mês, junto com o salário.
                    </p>
                  ) : (
                    <p className={`dpNotice ${fgts && fgts.difference !== null && Number(fgts.difference) !== 0 ? "dpNotice--warning" : ""}`}>
                      <strong>Conferência com as guias.</strong> FGTS calculado {formatCurrency(fgts?.calculated)}
                      {fgts?.paid !== null && fgts?.paid !== undefined
                        ? ` · guia lançada ${formatCurrency(fgts.paid)} · diferença ${formatCurrency(fgts.difference)}`
                        : " · guia ainda não lançada em Recolhimentos"}
                      . INSS dos empregados {formatCurrency(period.reconciliation.inss.employeesCalculated)}
                      {period.reconciliation.inss.darf !== null
                        ? ` · DARF ${formatCurrency(period.reconciliation.inss.darf)} (inclui a parte da empresa)`
                        : " · DARF ainda não lançado"}
                      .
                    </p>
                  )}

                  <FinanceBridgeBox
                    view={period.financeiro}
                    hint={
                      thirteenth
                        ? "Calcular, mudar a data e Fechar atualizam a previsão do líquido no Financeiro, por centro de custo; Reabrir cancela o que não tem baixa."
                        : "Calcular, mudar as datas e Fechar atualizam a previsão do adiantamento e do líquido no Financeiro, por centro de custo; Reabrir cancela o que não tem baixa."
                    }
                  />

                  {period.status === "FECHADO" && period.provisionSnapshot ? (
                    <p className={`dpNotice ${period.provisionSnapshot.count === 0 ? "dpNotice--warning" : ""}`}>
                      <strong>Provisões.</strong>{" "}
                      {period.provisionSnapshot.count > 0
                        ? `Foto do mês gravada no fechamento para ${period.provisionSnapshot.count} colaborador(es).`
                        : "A foto das provisões deste mês não foi gerada: use \"Gerar foto\" em Provisões."}{" "}
                      <button type="button" className="dpBtn dpBtn--secondary" onClick={() => navigate(DP_PROVISIONS_PAGE())}>
                        Abrir Provisões
                      </button>
                    </p>
                  ) : null}
                </>
              )}

              <nav className="dpTabs">
                <button className={`dpTab ${tab === "resultados" ? "is-active" : ""}`} onClick={() => setTab("resultados")}>
                  Colaboradores
                </button>
                <button className={`dpTab ${tab === "lancamentos" ? "is-active" : ""}`} onClick={() => setTab("lancamentos")}>
                  {thirteenth ? "Lançamentos" : "Lançamentos do mês"} ({entries.length})
                </button>
              </nav>

              {tab === "resultados" ? (
                <>
                  <div className="dpControls">
                    <input
                      className="dpInput"
                      placeholder="Buscar por nome, CPF ou matrícula"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      style={{ maxWidth: 320 }}
                    />
                    <select
                      className="dpSelect"
                      aria-label="Situação"
                      value={status}
                      onChange={(e) => setStatus(e.target.value as StatusFilter)}
                    >
                      <option value="TODOS">Todas as situações ({period.summary.contracts})</option>
                      {(["OK", "APURACAO_MANUAL", "SEM_PONTO", "PENDENTE", "RESCINDIDO"] as DpPayrollResultStatus[]).map((s) => (
                        <option key={s} value={s}>
                          {PAYROLL_RESULT_STATUS_LABELS[s]} ({period.summary.byStatus[s] ?? 0})
                        </option>
                      ))}
                    </select>
                    <button
                      className="dpBtn dpBtn--secondary"
                      onClick={() => exportCsv("resumo")}
                      disabled={!!busy || !!notCalculated}
                    >
                      {busy === "csv-resumo" ? "Exportando..." : "CSV resumo"}
                    </button>
                    <button
                      className="dpBtn dpBtn--secondary"
                      onClick={() => exportCsv("analitico")}
                      disabled={!!busy || !!notCalculated}
                    >
                      {busy === "csv-analitico" ? "Exportando..." : "CSV analítico"}
                    </button>
                    <DocumentFileButton
                      token={token}
                      fetchBlob={() => getPayslipsBatchPdfApi(token, period.id, status === "TODOS" ? undefined : status)}
                      fileName={`contracheques-${competenceSlug(period)}.pdf`}
                      label="Contracheques do mês (PDF)"
                      className="dpBtn dpBtn--secondary"
                      disabled={!!busy || !!notCalculated}
                      title="Todos os contracheques da situação escolhida num PDF só, para imprimir (quem não tem contracheque aparece na última página)"
                    />
                    {thirteenth ? null : (
                      <DocumentFileButton
                        token={token}
                        fetchBlob={() => getAdvanceReceiptsBatchPdfApi(token, period.id, status === "TODOS" ? undefined : status)}
                        fileName={`adiantamentos-${competenceSlug(period)}.pdf`}
                        label="Recibos do adiantamento (PDF)"
                        className="dpBtn dpBtn--secondary"
                        disabled={!!busy || !!notCalculated || Number(period.summary.advance) <= 0}
                        title="Recibos do adiantamento da situação escolhida num PDF só (valor do último cálculo)"
                      />
                    )}
                  </div>

                  <div className="dpTableWrapper">
                    <table className="dpTable">
                      <thead>
                        <tr>
                          <th>Colaborador</th>
                          <th>Centro de custo</th>
                          <th>Bruto</th>
                          <th>Descontos</th>
                          {thirteenth ? null : <th>Adiantamento</th>}
                          <th>Líquido final</th>
                          <th>PIX</th>
                          <th style={{ width: 80, textAlign: "center" }}>Copiado</th>
                          <th>Situação</th>
                          <th style={{ width: thirteenth ? 280 : 360 }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.length === 0 ? (
                          <tr>
                            <td colSpan={thirteenth ? 9 : 10} className="dpEmpty">
                              {notCalculated ? "Calcule a competência." : "Nenhum colaborador nesse filtro."}
                            </td>
                          </tr>
                        ) : (
                          visible.map((r) => (
                            <tr key={r.id}>
                              <td>
                                <strong>{r.meta.fullName}</strong>
                                <div className="dpFormHint">
                                  {r.meta.registrationNumber ? `Matrícula ${r.meta.registrationNumber} · ` : ""}
                                  {formatCpf(r.meta.cpf)}
                                </div>
                              </td>
                              <td>{r.meta.costCenter ? `${r.meta.costCenter.code} - ${r.meta.costCenter.name}` : "—"}</td>
                              {r.status === "SEM_PONTO" ? (
                                <td colSpan={thirteenth ? 3 : 4} className="dpFormHint">
                                  Aguardando apuração do mês — valores ocultos.
                                </td>
                              ) : r.status === "RESCINDIDO" ? (
                                <td colSpan={thirteenth ? 3 : 4} className="dpFormHint">
                                  Desligado no mês: o saldo de salário é pago no demonstrativo de rescisão.
                                </td>
                              ) : (
                                <>
                                  <td>{formatCurrency(r.grossAmount)}</td>
                                  <td>{formatCurrency(Number(r.deductionsAmount) - Number(r.advanceAmount))}</td>
                                  {thirteenth ? null : <td>{formatCurrency(r.advanceAmount)}</td>}
                                  <td>
                                    <strong style={Number(r.netAmount) < 0 ? { color: "var(--eco-danger-ink)" } : undefined}>
                                      {formatCurrency(r.netAmount)}
                                    </strong>
                                  </td>
                                </>
                              )}
                              <td>
                                {r.pixKey ? (
                                  <>
                                    <button
                                      className="dpBtn dpBtn--secondary dpBtn--small"
                                      onClick={() => copyPix(r)}
                                      disabled={pixBusy === r.contractId}
                                    >
                                      Copiar PIX
                                    </button>
                                    <div className="dpFormHint" style={{ wordBreak: "break-all" }}>
                                      {r.pixKey}
                                    </div>
                                  </>
                                ) : (
                                  <span className="dpFormHint" title="Cadastre a chave PIX na ficha do colaborador">
                                    Sem PIX
                                  </span>
                                )}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  aria-label={`PIX de ${r.meta.fullName} copiado`}
                                  checked={!!r.pixCopy}
                                  onChange={() => togglePixCopy(r)}
                                  disabled={pixBusy === r.contractId}
                                  title={
                                    r.pixCopy
                                      ? `Copiado em ${new Date(r.pixCopy.copiedAt).toLocaleString("pt-BR")}${
                                          r.pixCopy.copiedBy ? ` por ${r.pixCopy.copiedBy.nomeCompleto}` : ""
                                        }`
                                      : "PIX ainda não copiado"
                                  }
                                  style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#16a34a" }}
                                />
                              </td>
                              <td>
                                <span
                                  className={`dpBadge ${PAYROLL_RESULT_STATUS_BADGE[r.status]}`}
                                  title={r.warnings.join("\n")}
                                >
                                  {PAYROLL_RESULT_STATUS_LABELS[r.status]}
                                </span>
                                {r.warnings.length ? (
                                  <div className="dpFormHint">{r.warnings.length} aviso(s)</div>
                                ) : null}
                                {monthInputs[r.contractId] ? (
                                  <div className="dpFormHint">{describeMonthInput(monthInputs[r.contractId])}</div>
                                ) : null}
                              </td>
                              <td>
                                <div className="dpTable__actions">
                                  {isOpen && !thirteenth && r.status !== "RESCINDIDO" ? (
                                    <button
                                      className="dpBtn dpBtn--secondary dpBtn--small"
                                      onClick={() => setMonthInputModal({ contractId: r.contractId, employeeName: r.meta.fullName })}
                                      title="Digitar dias trabalhados, faltas e horas extras do mês"
                                    >
                                      {monthInputs[r.contractId] ? "Apuração" : "Apurar mês"}
                                    </button>
                                  ) : null}
                                  <DocumentFileButton
                                    token={token}
                                    fetchBlob={() => getPayslipPdfApi(token, period.id, r.contractId)}
                                    fileName={`contracheque-${competenceSlug(period)}.pdf`}
                                    label="Contracheque"
                                    className="dpBtn dpBtn--secondary dpBtn--small"
                                    disabled={!!PAYSLIP_BLOCKED[r.status]}
                                    title={
                                      PAYSLIP_BLOCKED[r.status] ??
                                      (isOpen ? "Abrir o contracheque para imprimir (prévia: mês aberto)" : "Abrir o contracheque para imprimir")
                                    }
                                  />
                                  {thirteenth ? null : (
                                    <DocumentFileButton
                                      token={token}
                                      fetchBlob={() => getAdvanceReceiptPdfApi(token, period.id, r.contractId)}
                                      fileName={`adiantamento-${competenceSlug(period)}.pdf`}
                                      label="Recibo do adiantamento"
                                      className="dpBtn dpBtn--secondary dpBtn--small"
                                      disabled={Number(r.advanceAmount) <= 0}
                                      title={
                                        Number(r.advanceAmount) > 0
                                          ? "Abrir o recibo do adiantamento para imprimir (valor do último cálculo)"
                                          : "Sem adiantamento nesta competência"
                                      }
                                    />
                                  )}
                                  <button
                                    className="dpBtn dpBtn--secondary dpBtn--small"
                                    onClick={() => navigate(DP_PAYROLL_RESULT_PAGE(period.id, r.contractId))}
                                  >
                                    Detalhe
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div className="dpControls">
                    {isOpen ? (
                      <button
                        className="dpBtn dpBtn--primary"
                        onClick={() => openEntry()}
                        disabled={!!busy || period.results.length === 0}
                        title={period.results.length === 0 ? "Calcule a competência antes de lançar" : undefined}
                      >
                        Novo lançamento
                      </button>
                    ) : null}
                  </div>
                  <p className="dpNotice">
                    Prêmios, gratificações e descontos eventuais deste mês. Descontos que se repetem (pensão,
                    consignado, plano de saúde) ficam na ficha do colaborador, aba Contrato. Depois de lançar,
                    clique em Calcular.
                  </p>
                  <div className="dpTableWrapper">
                    <table className="dpTable">
                      <thead>
                        <tr>
                          <th>Colaborador</th>
                          <th>Rubrica</th>
                          <th>Referência</th>
                          <th>Valor</th>
                          <th>Observação</th>
                          {isOpen ? <th style={{ width: 160 }}>Ações</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {entries.length === 0 ? (
                          <tr>
                            <td colSpan={isOpen ? 6 : 5} className="dpEmpty">
                              Nenhum lançamento neste mês.
                            </td>
                          </tr>
                        ) : (
                          entries.map((e) => (
                            <tr key={e.id}>
                              <td>{e.contract.employee.fullName}</td>
                              <td>
                                {e.item.name}
                                <div className="dpFormHint">{PAYROLL_ITEM_KIND_LABELS[e.item.kind]}</div>
                              </td>
                              <td>{e.quantity === null ? "—" : Number(e.quantity).toLocaleString("pt-BR")}</td>
                              <td>{formatCurrency(e.amount)}</td>
                              <td>{e.note || "—"}</td>
                              {isOpen ? (
                                <td>
                                  <div className="dpTable__actions">
                                    <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openEntry(e)}>
                                      Editar
                                    </button>
                                    <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => removeEntry(e)}>
                                      Excluir
                                    </button>
                                  </div>
                                </td>
                              ) : null}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <p className="dpFormHint" style={{ marginTop: 16 }}>
                {thirteenth
                  ? `Pagamento previsto para ${formatDateOnly(period.paymentDate)}.`
                  : `Adiantamento previsto para ${formatDateOnly(period.advanceDate)}; salário em ${formatDateOnly(period.paymentDate)}.`}
              </p>
            </>
          )}
        </div>
      </main>

      {monthInputModal && period && token ? (
        <MonthInputModal
          token={token}
          periodId={period.id}
          contractId={monthInputModal.contractId}
          employeeName={monthInputModal.employeeName}
          current={monthInputs[monthInputModal.contractId] ?? null}
          onClose={() => setMonthInputModal(null)}
          onSaved={(input) => {
            setMonthInputs((m) => {
              const next = { ...m };
              if (input) next[monthInputModal.contractId] = input;
              else delete next[monthInputModal.contractId];
              return next;
            });
            setMonthInputModal(null);
          }}
        />
      ) : null}

      {reopening && period ? (
        <ReasonModal
          title={`Reabrir a competência ${formatPayrollCompetence(period)}`}
          description="A competência volta a aceitar lançamentos e novo cálculo. O motivo fica registrado."
          confirmLabel="Reabrir"
          onClose={() => setReopening(false)}
          onConfirm={async (reason) => {
            await reopenPayrollPeriodApi(token, period.id, reason);
            toast.success("Competência reaberta.");
            setReopening(false);
            await load();
          }}
        />
      ) : null}

      {entryModal && period ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{entryModal.entry ? "Editar lançamento" : "Novo lançamento"}</h2>
              <button className="dpModal__close" onClick={() => setEntryModal(null)}>
                &times;
              </button>
            </header>
            <form onSubmit={saveEntry}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="entryContract">Colaborador *</label>
                  <select
                    id="entryContract"
                    className="dpSelect"
                    value={entryForm.contractId}
                    onChange={(e) => setEntryForm((f) => ({ ...f, contractId: e.target.value }))}
                    disabled={busy === "entry" || !!entryModal.entry}
                  >
                    <option value="">Selecione</option>
                    {[...period.results]
                      .sort((a, b) => a.meta.fullName.localeCompare(b.meta.fullName, "pt-BR"))
                      .map((r) => (
                        <option key={r.contractId} value={r.contractId}>
                          {r.meta.fullName}
                          {r.meta.registrationNumber ? ` (${r.meta.registrationNumber})` : ""}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="entryItem">Rubrica *</label>
                  <select
                    id="entryItem"
                    className="dpSelect"
                    value={entryForm.itemId}
                    onChange={(e) => setEntryForm((f) => ({ ...f, itemId: e.target.value }))}
                    disabled={busy === "entry"}
                  >
                    <option value="">Selecione</option>
                    {manualItems.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} ({PAYROLL_ITEM_KIND_LABELS[i.kind].toLowerCase()})
                      </option>
                    ))}
                  </select>
                  <span className="dpFormHint">
                    Rubrica que falta (prêmio, gratificação…) é criada em Cadastros → Rubricas.
                  </span>
                </div>
                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="entryAmount">Valor (R$) *</label>
                    <input
                      id="entryAmount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      className="dpInput"
                      value={entryForm.amount}
                      onChange={(e) => setEntryForm((f) => ({ ...f, amount: e.target.value }))}
                      disabled={busy === "entry"}
                    />
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="entryQuantity">Referência</label>
                    <input
                      id="entryQuantity"
                      type="number"
                      step="0.01"
                      min="0"
                      className="dpInput"
                      placeholder="horas, dias…"
                      value={entryForm.quantity}
                      onChange={(e) => setEntryForm((f) => ({ ...f, quantity: e.target.value }))}
                      disabled={busy === "entry"}
                    />
                  </div>
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="entryNote">Observação</label>
                  <textarea
                    id="entryNote"
                    className="dpTextarea"
                    maxLength={500}
                    value={entryForm.note}
                    onChange={(e) => setEntryForm((f) => ({ ...f, note: e.target.value }))}
                    disabled={busy === "entry"}
                  />
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setEntryModal(null)} disabled={busy === "entry"}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={busy === "entry"}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
